import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { schoolSettingsApi, schoolProfileApi } from '../../services/api';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Save, Settings as SettingsIcon, Upload, ImageIcon, KeyRound, Award, X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

export default function SettingsPage() {
  const { user, checkAuth } = useAuth();
  const fileRef = useRef(null);
  const boardLogoRef = useRef(null);
  const [logoUrl, setLogoUrl] = useState(user?.school?.logo_url || '');
  const [uploading, setUploading] = useState(false);
  const [uploadingBoardLogo, setUploadingBoardLogo] = useState(false);
  const [form, setForm] = useState({
    google_calendar_embed_url: '',
    whatsapp_default_group_url: '',
    receipt_prefix: '',
    board: 'CBSE',
    affiliation_no: '',
    logo_left_url: '',
    default_student_password: '',
    default_teacher_password: '',
    default_parent_password: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bulkResetting, setBulkResetting] = useState(null);

  const handleBulkResetPasswords = async (role) => {
    const defaults = {
      student: form.default_student_password || 'Student@123',
      teacher: form.default_teacher_password || 'Teacher@123',
      parent: form.default_parent_password || 'Parent@123',
    };
    const target = defaults[role];
    const ok = window.confirm(
      `This will RESET every existing ${role}'s password to "${target}" and they will use the SAME password going forward (until each user changes their own).\n\nAre you sure?`
    );
    if (!ok) return;
    setBulkResetting(role);
    try {
      const res = await api.post('/admin/users/bulk-reset-passwords', {
        role,
        new_password: target,
        force_change_on_next_login: false,
      });
      toast.success(`${res.data.updated} ${role} password(s) reset to "${target}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk reset failed');
    } finally {
      setBulkResetting(null);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await schoolSettingsApi.get();
        setForm({
          google_calendar_embed_url: res.data.google_calendar_embed_url || '',
          whatsapp_default_group_url: res.data.whatsapp_default_group_url || '',
          receipt_prefix: res.data.receipt_prefix || '',
          board: res.data.board || 'CBSE',
          affiliation_no: res.data.affiliation_no || '',
          logo_left_url: res.data.logo_left_url || '',
          default_student_password: res.data.default_student_password || '',
          default_teacher_password: res.data.default_teacher_password || '',
          default_parent_password: res.data.default_parent_password || '',
        });
      } catch (e) {
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await schoolSettingsApi.update(form);
      toast.success('Settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Extract iframe src if user pastes full <iframe ...> tag
  const handleEmbedPaste = (value) => {
    setForm({
      ...form,
      google_calendar_embed_url: value.match(/<iframe[^>]*src=["']([^"']+)["']/i)?.[1] || value,
    });
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await schoolProfileApi.uploadLogo(fd);
      setLogoUrl(r.data.logo_url);
      toast.success('School logo updated');
      // Refresh AuthContext so the header avatar reflects the new logo
      await checkAuth();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
          <SettingsIcon className="h-7 w-7 text-primary" /> School Settings
        </h1>
        <p className="text-muted-foreground mt-1">
          Configure Google Calendar embed, WhatsApp and receipt preferences.
        </p>
      </div>

      <Card className="border border-border" data-testid="school-logo-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-primary" /> School Logo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <div className="h-28 w-28 rounded border flex items-center justify-center bg-muted/30 overflow-hidden">
              {logoUrl ? (
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${logoUrl}`}
                  alt="School logo"
                  className="h-full w-full object-contain"
                  data-testid="school-logo-preview"
                />
              ) : (
                <span className="text-xs text-muted-foreground text-center px-2">No logo uploaded yet</span>
              )}
            </div>
            <div className="space-y-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => handleLogoUpload(e.target.files?.[0])}
              />
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="upload-school-logo-button"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                {logoUrl ? 'Replace logo' : 'Upload logo'}
              </Button>
              <p className="text-xs text-muted-foreground max-w-md">
                PNG/JPG/WEBP. Recommended: square, at least 500×500. The logo appears
                in the top-right of the dashboard and on every PDF (marksheet, fee receipt).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle>Google Calendar</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Label>Embed URL or &lt;iframe&gt; snippet</Label>
          <Input
            value={form.google_calendar_embed_url}
            onChange={(e) => handleEmbedPaste(e.target.value)}
            placeholder="https://calendar.google.com/calendar/embed?src=..."
            data-testid="gcal-embed-input"
          />
          <p className="text-xs text-muted-foreground">
            Paste either the calendar's embed URL or the full iframe code from
            Google Calendar → Settings → Integrate Calendar → "Embed code".
          </p>
        </CardContent>
      </Card>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle>WhatsApp</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Label>Default class WhatsApp group invite link</Label>
          <Input
            value={form.whatsapp_default_group_url}
            onChange={(e) =>
              setForm({ ...form, whatsapp_default_group_url: e.target.value })
            }
            placeholder="https://chat.whatsapp.com/XXXXXXXXXX"
            data-testid="whatsapp-default-url-input"
          />
          <p className="text-xs text-muted-foreground">
            Used as a fallback when a class doesn't have its own WhatsApp group
            link set. Teachers can override this per class in Classes → Edit.
          </p>
        </CardContent>
      </Card>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle>Fee Receipts</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Label>Receipt number prefix</Label>
          <Input
            value={form.receipt_prefix}
            onChange={(e) => setForm({ ...form, receipt_prefix: e.target.value.toUpperCase() })}
            placeholder="BLDS"
            data-testid="receipt-prefix-input"
          />
          <p className="text-xs text-muted-foreground">
            Final format: <code>{(form.receipt_prefix || 'PREFIX') + '-YYYY-MM-DD-NNNN'}</code>
          </p>
        </CardContent>
      </Card>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5 text-primary" />
            Board & Affiliation (Marksheet + Fee Receipt Header)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Board</Label>
              <Select value={form.board} onValueChange={(v) => setForm({ ...form, board: v })}>
                <SelectTrigger data-testid="board-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CBSE">CBSE</SelectItem>
                  <SelectItem value="ICSE">ICSE</SelectItem>
                  <SelectItem value="State Board">State Board</SelectItem>
                  <SelectItem value="IB">IB (International Baccalaureate)</SelectItem>
                  <SelectItem value="IGCSE">IGCSE (Cambridge)</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {form.board === 'CBSE'
                  ? 'CBSE logo shows on the left of PDF headers by default.'
                  : `${form.board} schools: left slot is empty unless you upload a custom logo below.`}
              </p>
            </div>
            <div>
              <Label>{form.board === 'Other' ? 'Affiliation No. (optional)' : `${form.board} Affiliation No.`}</Label>
              <Input
                value={form.affiliation_no}
                onChange={(e) => setForm({ ...form, affiliation_no: e.target.value })}
                placeholder="e.g. 830900"
                data-testid="affiliation-no-input"
              />
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Board Logo (Left side of PDF header) — optional</Label>
            <div className="flex items-center gap-3">
              {form.logo_left_url ? (
                <div className="relative">
                  <img
                    src={form.logo_left_url.startsWith('http') ? form.logo_left_url :
                         `${process.env.REACT_APP_BACKEND_URL || ''}${form.logo_left_url}`}
                    alt="board logo"
                    className="w-16 h-16 object-contain border rounded bg-white p-1"
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await api.delete('/schools/board-logo');
                        setForm({ ...form, logo_left_url: '' });
                        toast.success('Custom board logo removed');
                      } catch (e) {
                        toast.error('Failed to remove');
                      }
                    }}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                    title="Remove"
                    data-testid="board-logo-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <div className="w-16 h-16 border-2 border-dashed rounded flex items-center justify-center bg-muted/40">
                  <ImageIcon className="h-6 w-6 text-muted-foreground" />
                </div>
              )}
              <div className="flex-1">
                <input
                  ref={boardLogoRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  data-testid="board-logo-input"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setUploadingBoardLogo(true);
                    try {
                      const fd = new FormData();
                      fd.append('file', file);
                      const res = await api.post('/schools/board-logo', fd, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                      });
                      setForm({ ...form, logo_left_url: res.data.logo_left_url });
                      toast.success('Board logo uploaded');
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Upload failed');
                    } finally {
                      setUploadingBoardLogo(false);
                      e.target.value = '';
                    }
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => boardLogoRef.current?.click()}
                  disabled={uploadingBoardLogo}
                  data-testid="board-logo-upload-btn"
                >
                  {uploadingBoardLogo
                    ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    : <Upload className="h-4 w-4 mr-2" />}
                  {form.logo_left_url ? 'Replace Board Logo' : 'Upload Board Logo'}
                </Button>
                <p className="text-xs text-muted-foreground mt-1">
                  PNG / JPG / WEBP · used on the left side of Marksheet + Fee Receipt.
                  {form.board !== 'CBSE' && !form.logo_left_url &&
                    ' Leave empty and the left slot will simply be hidden.'}
                </p>
              </div>
            </div>
          </div>

          <p className="text-xs text-muted-foreground border-t pt-2">
            The affiliation line prints as <code>{form.board === 'Other' ? 'Affiliation No.' : `${form.board} Affiliation No.`} {form.affiliation_no || '...'}</code> on every PDF.
          </p>
        </CardContent>
      </Card>

      <Card className="border border-border" data-testid="default-passwords-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-primary" /> Default Passwords for New Accounts
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">
            When you add a new student, teacher, or parent (manually or via bulk
            import), their account is auto-created with the password below. The
            user is forced to change it on first login. Leave blank to use the
            global default (<code>student123</code> / <code>teacher123</code> /
            <code> parent123</code>).
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label>Student default</Label>
              <Input
                value={form.default_student_password}
                onChange={(e) => setForm({ ...form, default_student_password: e.target.value })}
                placeholder="student123"
                data-testid="default-student-password-input"
              />
            </div>
            <div className="space-y-1">
              <Label>Teacher default</Label>
              <Input
                value={form.default_teacher_password}
                onChange={(e) => setForm({ ...form, default_teacher_password: e.target.value })}
                placeholder="teacher123"
                data-testid="default-teacher-password-input"
              />
            </div>
            <div className="space-y-1">
              <Label>Parent default</Label>
              <Input
                value={form.default_parent_password}
                onChange={(e) => setForm({ ...form, default_parent_password: e.target.value })}
                placeholder="parent123"
                data-testid="default-parent-password-input"
              />
            </div>
          </div>
          <div className="pt-4 border-t">
            <p className="text-sm font-medium mb-2">
              Bulk Reset Existing Passwords
            </p>
            <p className="text-xs text-muted-foreground mb-3">
              Resets every existing user of the chosen role to the default
              password above (or <code>Student@123</code> / <code>Teacher@123</code>
              / <code>Parent@123</code> if blank). Useful when staff need to
              access all student/parent logins.
            </p>
            <div className="flex flex-wrap gap-2">
              {['student', 'teacher', 'parent'].map((role) => (
                <Button
                  key={role}
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkResetPasswords(role)}
                  disabled={bulkResetting === role}
                  data-testid={`bulk-reset-${role}-button`}
                >
                  {bulkResetting === role ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <KeyRound className="h-4 w-4 mr-2" />
                  )}
                  Reset all {role}s
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} data-testid="save-settings-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Save Settings
        </Button>
      </div>
    </div>
  );
}
